import { Fragment, type ReactNode, type RefObject } from 'react';
import { labels } from './labels';
import { LoadError } from './load-error';
import { Pager, usePagedList, type CardPage } from './use-paged-list';

export type { CardPage };

/**
 * QA-69: the one paging control for a list of document cards, on **Test extraction** and on
 * **Documents**. The cursor stack is `usePagedList` (QA-85 shares it with the **Not processed**
 * tables).
 *
 * - `renderCard` gets `reload`, which reads the page on the screen again (after a remove).
 * - `above` is a slot over the list, for the date input of **Documents**.
 * - QA-99: an empty later page says "No older items." (`texts.empty` is for the first page).
 */
export function PagedCardList<T extends { docId: string }>({
  load,
  renderCard,
  version = 0,
  poll = false,
  above,
  texts,
  focusTarget,
}: {
  load: (cursor: string | undefined, signal: AbortSignal) => Promise<CardPage<T>>;
  renderCard: (item: T, reload: () => void) => ReactNode;
  version?: unknown;
  poll?: boolean;
  above?: ReactNode;
  texts: { loading: string; empty: string; failed: string };
  /** Where focus goes when a page change leaves no pager button (QA-99). Needs `tabIndex={-1}`. */
  focusTarget?: RefObject<HTMLElement | null>;
}) {
  const list = usePagedList(load, version, poll, focusTarget);
  const { page, failed, reload } = list;

  return (
    <>
      {above}
      {failed && !page && <LoadError text={texts.failed} onRetry={list.retry} />}
      {!failed && !page && <p className="notice">{texts.loading}</p>}
      {page && page.items.length === 0 && <p className="notice">{list.first ? texts.empty : labels.paging.noOlder}</p>}
      {page && page.items.length > 0 && (
        <ul className="doc-list flow-md">
          {page.items.map((item) => (
            <Fragment key={item.docId}>{renderCard(item, reload)}</Fragment>
          ))}
        </ul>
      )}
      <Pager list={list} focusTarget={focusTarget} />
    </>
  );
}
